import { useAppSSE } from 'contexts/AppSSEContext';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  createVoiceRoom,
  endVoiceRoom,
  getVoiceRooms,
  leaveVoiceRoom,
} from '../api/voice.api';
import { isResponderVoipEnabled } from '../config/voipFeatureFlag';
import { useCamera } from '../hooks/useCamera';
import { useNoiseGate } from '../hooks/useNoiseGate';
import {
  closeMessengerNotification,
  showMessengerNotification,
  startIncomingCallRingtone,
  stopIncomingCallRingtone,
} from '../utils/messengerNotificationUtils';
import { setMessengerTitleIncomingCall } from '../utils/messengerTitleUtils';

const VoiceContext = createContext(null);

// In prod, PUBLIC_API_URL is the absolute backend URL (e.g. https://aggapi.overwatch.ai/fusion)
// and the WS connects directly. In dev, rsbuild rewrites PUBLIC_API_URL to a relative path
// (e.g. /stagingdemo/fusion) so requests route through the dev proxy — including WS upgrades —
// keeping the team prefix on the URL so the aggregator can route it correctly.
const buildWsBaseUrl = () => {
  const apiUrl = process.env.PUBLIC_API_URL || '';
  if (apiUrl.startsWith('https://')) return apiUrl.replace(/^https/, 'wss');
  if (apiUrl.startsWith('http://')) return apiUrl.replace(/^http/, 'ws');
  if (apiUrl.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${apiUrl}`;
  }
  return '';
};

const WS_BASE_URL = buildWsBaseUrl();

const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];
const VOIP_ENABLED = isResponderVoipEnabled();

const normalizePeerId = (value) => (value == null ? null : String(value));

const getParticipantUserId = (participant) =>
  participant?.userId ?? participant?.user_id ?? participant?.user?.id ?? null;

const getSignalUserId = (payload) =>
  payload?.userId ?? payload?.user_id ?? payload?.user?.id ?? null;

const getSignalFromUserId = (payload) =>
  payload?.fromUserId ??
  payload?.from_user_id ??
  payload?.from?.userId ??
  payload?.from?.user_id ??
  payload?.from?.id ??
  null;

function VoiceCoordinator({ children, currentUserId }) {
  const { subscribe } = useAppSSE();

  const [callStatus, setCallStatus] = useState('idle'); // 'idle' | 'initiated' | 'connecting' | 'in-call'
  const callStatusRef = useRef('idle');
  const [activeRoom, setActiveRoom] = useState(null); // { id, name, conversationId, participants }
  const [isMuted, setIsMuted] = useState(() => localStorage.getItem('voip_mic_muted') === 'true');
  const [availableCalls, setAvailableCalls] = useState([]); // [{ roomId, conversationId, roomName, startedBy }]
  const [isCreator, setIsCreator] = useState(false);

  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef(new Map()); // userId -> RTCPeerConnection
  const peerSignalIdsRef = useRef(new Map()); // normalized userId -> original signalling userId
  const remoteAudioElementsRef = useRef(new Map()); // userId -> HTMLAudioElement
  const videoReofferTimersRef = useRef(new Map()); // normalized userId -> timeout id
  const activeRoomRef = useRef(null);
  const availableCallsRef = useRef([]);
  const [remoteVideoStreams, setRemoteVideoStreams] = useState(new Map()); // userId -> MediaStream
  const [connectingPeers, setConnectingPeers] = useState(new Set()); // userIds mid-negotiation

  // Keep refs in sync for use in WS/SSE callbacks
  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  useEffect(() => {
    availableCallsRef.current = availableCalls;
  }, [availableCalls]);

  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  const cleanupPeerConnection = useCallback((userId) => {
    const peerId = normalizePeerId(userId);
    const videoReofferTimer = videoReofferTimersRef.current.get(peerId);
    if (videoReofferTimer) {
      clearTimeout(videoReofferTimer);
      videoReofferTimersRef.current.delete(peerId);
    }
    const pc = peerConnectionsRef.current.get(peerId);
    if (pc) {
      pc.close();
      peerConnectionsRef.current.delete(peerId);
      peerSignalIdsRef.current.delete(peerId);
    }
    const audio = remoteAudioElementsRef.current.get(peerId);
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      remoteAudioElementsRef.current.delete(peerId);
    }
    setRemoteVideoStreams((prev) => {
      if (!prev.has(peerId)) return prev;
      const next = new Map(prev);
      next.delete(peerId);
      return next;
    });
    setConnectingPeers((prev) => {
      if (!prev.has(peerId)) return prev;
      const next = new Set(prev);
      next.delete(peerId);
      return next;
    });
  }, []);

  const sendWsMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const renegotiate = useCallback(
    async (pc, targetUserId) => {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWsMessage({ type: 'offer', targetUserId, offer });
    },
    [sendWsMessage],
  );

  const onTrackReady = useCallback(
    async (track, stream) => {
      for (const [targetPeerId, pc] of peerConnectionsRef.current) {
        const targetUserId = peerSignalIdsRef.current.get(targetPeerId) ?? targetPeerId;
        pc.addTrack(track, stream);
        await renegotiate(pc, targetUserId);
      }
    },
    [renegotiate],
  );

  const onTrackStopped = useCallback(async () => {
    for (const [targetPeerId, pc] of peerConnectionsRef.current) {
      const targetUserId = peerSignalIdsRef.current.get(targetPeerId) ?? targetPeerId;
      const videoSenders = pc.getSenders().filter((s) => s.track?.kind === 'video');
      videoSenders.forEach((s) => pc.removeTrack(s));
      await renegotiate(pc, targetUserId);
    }
  }, [renegotiate]);

  const { cameraOn, setCameraOn, toggleCamera, localVideoStreamRef, stopCamera } = useCamera({
    onTrackReady,
    onTrackStopped,
  });

  const { startGate, stopGate } = useNoiseGate(localStreamRef);

  const attachCurrentLocalVideoTrack = useCallback(
    async (pc, targetUserId, targetPeerId, reason, options = {}) => {
      const { forceVideoDirection = null } = options;
      const stream = localVideoStreamRef.current;
      const track = stream
        ?.getVideoTracks?.()
        .find((videoTrack) => videoTrack.readyState !== 'ended');

      if (!track) {
        return false;
      }

      const existingVideoSender = pc.getSenders?.().find((sender) => sender.track?.kind === 'video');
      if (existingVideoSender?.track === track) {
        return false;
      }

      if (existingVideoSender?.replaceTrack) {
        await existingVideoSender.replaceTrack(track);
        return true;
      }

      const videoTransceiver = pc
        .getTransceivers?.()
        .find(
          (transceiver) =>
            transceiver.sender?.track?.kind === 'video' ||
            transceiver.receiver?.track?.kind === 'video',
        );

      if (videoTransceiver?.sender?.replaceTrack) {
        await videoTransceiver.sender.replaceTrack(track);
        if (forceVideoDirection) {
          videoTransceiver.direction = forceVideoDirection;
        } else if (videoTransceiver.direction === 'recvonly') {
          videoTransceiver.direction = 'sendrecv';
        } else if (videoTransceiver.direction === 'inactive') {
          videoTransceiver.direction = 'sendonly';
        }
        return true;
      }

      pc.addTrack(track, stream);
      return true;
    },
    [localVideoStreamRef],
  );

  const cleanupAll = useCallback(() => {
    videoReofferTimersRef.current.forEach((timer) => clearTimeout(timer));
    videoReofferTimersRef.current.clear();
    peerConnectionsRef.current.forEach((_, userId) => cleanupPeerConnection(userId));
    peerConnectionsRef.current.clear();
    peerSignalIdsRef.current.clear();

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    stopGate();
    stopCamera();
    setCameraOn(false);
    setCallStatus('idle');
    setActiveRoom(null);
    setIsCreator(false);
    setRemoteVideoStreams((prev) => (prev.size ? new Map() : prev));
  }, [cleanupPeerConnection, stopCamera, setCameraOn, stopGate]);

  const createPeerConnection = useCallback(
    (targetUserId, options = {}) => {
      const { deferLocalVideo = false } = options;
      const targetPeerId = normalizePeerId(targetUserId);
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

      // Add local tracks
      const localTracks = localStreamRef.current?.getTracks() || [];
      const addedTracks = new Set();
      localTracks.forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
        addedTracks.add(track);
      });

      const localVideoTracks = localVideoStreamRef.current?.getTracks?.() || [];
      if (deferLocalVideo) {
        // Incoming offers create the remote video transceiver when setRemoteDescription runs.
        // Attach our current camera track after that so the answer advertises video correctly.
      } else {
        localVideoTracks.forEach((track) => {
          if (addedTracks.has(track)) return;
          pc.addTrack(track, localVideoStreamRef.current);
          addedTracks.add(track);
        });
      }
      if (localVideoTracks.length === 0 && !deferLocalVideo) {
        pc.addTransceiver?.('video', { direction: 'recvonly' });
      }

      // Play remote audio/video
      pc.ontrack = (event) => {
        const stream = event.streams[0] ?? new MediaStream([event.track]);

        if (event.track.kind === 'video') {
          const clearVideo = () => {
            setRemoteVideoStreams((prev) => {
              if (!prev.has(targetPeerId)) return prev;
              const next = new Map(prev);
              next.delete(targetPeerId);
              return next;
            });
          };
          event.track.onended = clearVideo;
          setRemoteVideoStreams((prev) => {
            const next = new Map(prev);
            next.set(targetPeerId, stream);
            return next;
          });
          return;
        }

        let audio = remoteAudioElementsRef.current.get(targetPeerId);
        if (!audio) {
          audio = document.createElement('audio');
          audio.autoplay = true;
          document.body.appendChild(audio);
          remoteAudioElementsRef.current.set(targetPeerId, audio);
        }
        audio.srcObject = stream;
        const playPromise = audio.play?.();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch((e) => {
            console.error('[VoiceWebRTC] remote audio play() rejected', {
              targetUserId: targetPeerId,
              name: e?.name,
              message: e?.message,
            });
          });
        }
      };

      setConnectingPeers((prev) => new Set([...prev, targetPeerId]));

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          setConnectingPeers((prev) => {
            const next = new Set(prev);
            next.delete(targetPeerId);
            return next;
          });
          setCallStatus('in-call');
        }
      };

      // Send ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendWsMessage({ type: 'ice-candidate', targetUserId, candidate: event.candidate });
        }
      };

      peerConnectionsRef.current.set(targetPeerId, pc);
      peerSignalIdsRef.current.set(targetPeerId, targetUserId);
      return pc;
    },
    [localVideoStreamRef, sendWsMessage],
  );

  const offerCurrentLocalVideoToPeer = useCallback(
    async (targetUserId, reason) => {
      const targetPeerId = normalizePeerId(targetUserId);
      if (!targetPeerId) return;

      const stream = localVideoStreamRef.current;
      const track = stream
        ?.getVideoTracks?.()
        .find((videoTrack) => videoTrack.readyState !== 'ended');

      if (!track) {
        return 'no-local-video';
      }

      let pc = peerConnectionsRef.current.get(targetPeerId);
      if (!pc) {
        pc = createPeerConnection(targetUserId);
      }

      if (pc.signalingState && pc.signalingState !== 'stable') {
        return 'unstable-signaling';
      }

      await attachCurrentLocalVideoTrack(pc, targetUserId, targetPeerId, reason);
      await renegotiate(pc, targetUserId);
      return 'sent';
    },
    [attachCurrentLocalVideoTrack, createPeerConnection, localVideoStreamRef, renegotiate],
  );

  const scheduleVideoReofferToPeer = useCallback(
    (targetUserId, reason) => {
      const targetPeerId = normalizePeerId(targetUserId);
      if (!targetPeerId) return;

      const existingTimer = videoReofferTimersRef.current.get(targetPeerId);
      if (existingTimer) clearTimeout(existingTimer);

      const queueAttempt = (attempt, delay) => {
        const timer = setTimeout(() => {
          videoReofferTimersRef.current.delete(targetPeerId);
          offerCurrentLocalVideoToPeer(targetUserId, reason)
            .then((result) => {
              if (result === 'unstable-signaling' && attempt < 3) {
                queueAttempt(attempt + 1, 750);
              }
            })
            .catch(() => {});
        }, delay);

        videoReofferTimersRef.current.set(targetPeerId, timer);
      };

      queueAttempt(1, 750);
    },
    [offerCurrentLocalVideoToPeer],
  );

  const initiateOffer = useCallback(
    async (targetUserId) => {
      const pc = createPeerConnection(targetUserId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWsMessage({ type: 'offer', targetUserId, offer });
    },
    [createPeerConnection, sendWsMessage],
  );

  const handleWsMessage = useCallback(
    async (raw) => {
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }

      const { type } = msg;

      if (type === 'participants') {
        // We just joined — initiate offers to everyone already in the room.
        // If we're the creator in 'initiated' (ringing) state and no one else is here yet,
        // stay in 'initiated' so the UI keeps showing "Ringing...".
        const others = msg.participants || [];
        for (const participant of others) {
          const participantUserId = getParticipantUserId(participant);
          if (participantUserId == null) continue;
          await initiateOffer(participantUserId);
        }
        setActiveRoom((prev) =>
          prev
            ? {
                ...prev,
                participants: others.map((participant) => ({
                  ...participant,
                  userId: getParticipantUserId(participant),
                })),
              }
            : prev,
        );
        // Stay in 'connecting' — RTCPeerConnection onconnectionstatechange drives the 'in-call' transition.
        // Only skip past 'initiated' (ringing, no peers yet) when the room is empty.
        if (callStatusRef.current === 'initiated' && others.length > 0) {
          setCallStatus('connecting');
        } else if (callStatusRef.current === 'connecting') {
          setCallStatus('in-call');
        }
      } else if (type === 'user-joined') {
        const userId = getSignalUserId(msg);
        if (userId == null) return;
        const peerId = normalizePeerId(userId);
        const existingPc = peerConnectionsRef.current.get(peerId);
        if (existingPc) {
          cleanupPeerConnection(userId);
        }
        // New peer arrived — move to in-call regardless of current ringing/connecting state
        if (callStatusRef.current === 'initiated' || callStatusRef.current === 'connecting') {
          setCallStatus('in-call');
        }
        setActiveRoom((prev) => {
          if (!prev) return prev;
          const already = prev.participants.some((p) => `${p.userId}` === `${userId}`);
          if (already) return prev;
          return {
            ...prev,
            participants: [
              ...prev.participants,
              { userId, username: msg.username, joinedAt: msg.joinedAt },
            ],
          };
        });
        scheduleVideoReofferToPeer(userId, 'streaming-peer-saw-user-joined');
      } else if (type === 'call-ended') {
        cleanupAll();
      } else if (type === 'user-left') {
        const userId = getSignalUserId(msg);
        if (userId == null) return;
        cleanupPeerConnection(userId);
        setActiveRoom((prev) =>
          prev
            ? {
                ...prev,
                participants: prev.participants.filter((p) => `${p.userId}` !== `${userId}`),
              }
            : prev,
        );
      } else if (type === 'offer') {
        const fromUserId = getSignalFromUserId(msg);
        if (fromUserId == null) return;
        const fromPeerId = normalizePeerId(fromUserId);
        const existingPc = peerConnectionsRef.current.get(fromPeerId);
        if (existingPc?.connectionState === 'closed' || existingPc?.signalingState === 'closed') {
          cleanupPeerConnection(fromUserId);
        }
        const pc = peerConnectionsRef.current.get(fromPeerId) ?? createPeerConnection(fromUserId, { deferLocalVideo: true });
        await pc.setRemoteDescription(new RTCSessionDescription(msg.offer));
        await attachCurrentLocalVideoTrack(pc, fromUserId, fromPeerId, 'answering-offer', {
          forceVideoDirection: 'sendonly',
        });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendWsMessage({ type: 'answer', targetUserId: fromUserId, answer });
        scheduleVideoReofferToPeer(fromUserId, 'streaming-peer-answered-offer');
      } else if (type === 'answer') {
        const fromUserId = getSignalFromUserId(msg);
        if (fromUserId == null) return;
        const fromPeerId = normalizePeerId(fromUserId);
        const pc = peerConnectionsRef.current.get(fromPeerId);
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.answer));
        }
      } else if (type === 'ice-candidate') {
        const fromUserId = getSignalFromUserId(msg);
        if (fromUserId == null) return;
        const fromPeerId = normalizePeerId(fromUserId);
        const pc = peerConnectionsRef.current.get(fromPeerId);
        if (!pc) {
          console.warn('[VoiceWebRTC] ICE candidate received but no peer connection', {
            fromUserId,
          });
          return;
        }
        if (!msg.candidate) return;
        try {
          await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
        } catch (e) {
          console.error('[VoiceWebRTC] addIceCandidate failed', {
            fromUserId,
            name: e?.name,
            message: e?.message,
            signalingState: pc.signalingState,
            hasRemoteDescription: !!pc.remoteDescription,
          });
        }
      }
    },
    [
      initiateOffer,
      createPeerConnection,
      attachCurrentLocalVideoTrack,
      scheduleVideoReofferToPeer,
      cleanupPeerConnection,
      cleanupAll,
      sendWsMessage,
    ],
  );

  const openWebSocket = useCallback(
    (roomId) => {
      try {
        const url = `${WS_BASE_URL}/messaging/voice/signal/${roomId}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          handleWsMessage(event.data);
        };
        ws.onerror = (e) => {
          console.error('[VoiceWS] error', { type: e.type, url: ws.url, readyState: ws.readyState });
        };
        ws.onclose = (e) => {
          console.warn('[VoiceWS] closed', { code: e.code, reason: e.reason, wasClean: e.wasClean });
          if (activeRoomRef.current) cleanupAll();
        };
      } catch (err) {
        console.error('[VoiceWS] failed to open', err);
        cleanupAll();
      }
    },
    [handleWsMessage, cleanupAll],
  );

  const startCall = useCallback(
    async (conversationId, conversationName) => {
      if (!VOIP_ENABLED) return;
      if (callStatus !== 'idle') return;
      setCallStatus('connecting');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        stream.getAudioTracks().forEach((t) => { t.enabled = !isMuted; });
        startGate(stream);

        const data = await createVoiceRoom(conversationName || 'Call', conversationId);
        const room = data.room;

        setIsCreator(true);
        setActiveRoom({
          id: room.id,
          name: room.name,
          conversationId,
          participants: [],
          createdAt: room.created_at ?? room.createdAt ?? null,
        });

        setCallStatus('initiated');
        openWebSocket(room.id);
      } catch (err) {
        console.error('[VoiceContext] startCall failed:', err);
        cleanupAll();
      }
    },
    [callStatus, cleanupAll, openWebSocket, isMuted, startGate],
  );

  const joinCall = useCallback(
    async (roomId, conversationId, createdAt = null) => {
      if (!VOIP_ENABLED) return;
      if (callStatus !== 'idle') return;
      setCallStatus('connecting');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        stream.getAudioTracks().forEach((t) => { t.enabled = !isMuted; });
        startGate(stream);

        setActiveRoom({ id: roomId, name: '', conversationId, participants: [], createdAt });

        openWebSocket(roomId);
        setAvailableCalls((prev) => prev.filter((c) => c.roomId !== roomId));
        closeMessengerNotification(`call:${roomId}`);
      } catch (err) {
        console.error('[VoiceContext] joinCall failed:', err);
        cleanupAll();
      }
    },
    [callStatus, openWebSocket, cleanupAll, isMuted, startGate],
  );

  const leaveCall = useCallback(async () => {
    if (!VOIP_ENABLED) return;
    const room = activeRoomRef.current;
    sendWsMessage({ type: 'leave' });
    cleanupAll();
    if (room) {
      // Re-add to availableCalls so the user can rejoin
      setAvailableCalls((prev) => {
        if (prev.some((c) => c.roomId === room.id)) return prev;
        return [...prev, { roomId: room.id, conversationId: room.conversationId, roomName: room.name, startedBy: null }];
      });
      try {
        await leaveVoiceRoom(room.id);
      } catch (err) {
        console.error('[VoiceContext] leaveCall failed:', err);
      }
    }
  }, [sendWsMessage, cleanupAll]);

  const endCall = useCallback(async () => {
    if (!VOIP_ENABLED) return;
    const room = activeRoomRef.current;
    if (!room) return;
    try {
      await endVoiceRoom(room.id);
      cleanupAll();
    } catch (err) {
      console.error('[VoiceContext] endCall failed:', err);
    }
  }, [cleanupAll]);

  const toggleMute = useCallback(() => {
    if (!VOIP_ENABLED) return;
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsMuted((prev) => {
      const next = !prev;
      localStorage.setItem('voip_mic_muted', String(next));
      return next;
    });
  }, []);

  const dismissIncomingCall = useCallback((roomId) => {
    setAvailableCalls((prev) => {
      if (roomId == null) return [];
      return prev.filter((c) => c.roomId !== roomId);
    });
  }, []);

  const normalizeCallEvent = useCallback((data) => {
    const payload = data || {};
    const roomId = payload.roomId ?? payload.room_id ?? payload.room?.id ?? null;
    const conversationId =
      payload.conversationId ?? payload.conversation_id ?? payload.conversation?.id ?? null;
    const roomName =
      payload.roomName ?? payload.room_name ?? payload.room?.name ?? payload.name ?? '';
    const startedBy =
      payload.createdByName ??
      payload.startedBy ??
      payload.created_by_name ??
      payload.createdBy?.name ??
      'Someone';
    return { roomId, conversationId, roomName, startedBy };
  }, []);

  // ── SSE handlers ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!VOIP_ENABLED) return undefined;

    const unsubCallStarted = subscribe('call-started', (data) => {
      const { conversationId, roomId, roomName, startedBy } = normalizeCallEvent(data);
      if (!roomId) return;
      if (currentUserId && `${data?.createdBy}` === `${currentUserId}`) return;
      // Don't add banner while already in this call
      if (activeRoomRef.current?.id === roomId) return;
      if (availableCallsRef.current.some((c) => c.roomId === roomId)) return;

      showMessengerNotification({
        title: startedBy ? `Incoming call from ${startedBy}` : 'Incoming call',
        body: roomName || 'Responder voice call',
        tag: `call:${roomId}`,
        requireInteraction: true,
      });

      setAvailableCalls((prev) => {
        if (prev.some((c) => c.roomId === roomId)) return prev;
        return [...prev, { roomId, conversationId, roomName, startedBy }];
      });
    });

    const unsubCallEnded = subscribe('call-ended', (data) => {
      const { roomId } = normalizeCallEvent(data);
      if (!roomId) return;
      // Only dismiss the banner for observers — active participants are cleaned up via WS call-ended
      setAvailableCalls((prev) => prev.filter((c) => c.roomId !== roomId));
    });

    const unsubParticipantJoined = subscribe('call-participant-joined', (data) => {
      const { roomId } = normalizeCallEvent(data);
      const userId = data?.userId ?? data?.user_id;
      const username = data?.username ?? data?.user?.name;
      if (activeRoomRef.current?.id !== roomId) return;

      setActiveRoom((prev) => {
        if (!prev) return prev;
        const already = prev.participants.some((p) => `${p.userId}` === `${userId}`);
        if (already) return prev;
        return {
          ...prev,
          participants: [...prev.participants, { userId, username }],
        };
      });
    });

    const unsubParticipantLeft = subscribe('call-participant-left', (data) => {
      const { roomId, userId } = data || {};
      if (activeRoomRef.current?.id !== roomId) return;
      cleanupPeerConnection(userId);
      setActiveRoom((prev) =>
        prev
          ? {
              ...prev,
              participants: prev.participants.filter((p) => `${p.userId}` !== `${userId}`),
            }
          : prev,
      );
    });

    return () => {
      unsubCallStarted();
      unsubCallEnded();
      unsubParticipantJoined();
      unsubParticipantLeft();
    };
  }, [subscribe, cleanupAll, cleanupPeerConnection, normalizeCallEvent, currentUserId]);

  // Populate available calls on mount (e.g. page reload while calls are ongoing)
  useEffect(() => {
    getVoiceRooms()
      .then((data) => {
        const rooms = (data?.rooms ?? []).filter((r) => r.status === 'active');
        const normalizedRooms = rooms.map((r) => ({
          roomId: r.id,
          conversationId: r.conversationId,
          roomName: r.name,
          startedBy: null,
          createdAt: r.created_at ?? r.createdAt ?? null,
        }));
        setAvailableCalls((prev) => {
          const nextByRoomId = new Map(
            (Array.isArray(prev) ? prev : []).map((room) => [`${room?.roomId}`, room]),
          );
          normalizedRooms.forEach((room) => {
            nextByRoomId.set(`${room.roomId}`, {
              ...room,
              startedBy: nextByRoomId.get(`${room.roomId}`)?.startedBy ?? room.startedBy,
            });
          });
          return Array.from(nextByRoomId.values());
        });
      })
      .catch((e) => console.error('[VoiceContext] getVoiceRooms failed:', e));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    if (!VOIP_ENABLED) return undefined;
    return () => cleanupAll();
  }, [cleanupAll]);

  useEffect(() => {
    const hasIncomingCall = callStatus === 'idle' && availableCalls.length > 0;
    setMessengerTitleIncomingCall(hasIncomingCall);
    if (hasIncomingCall) {
      startIncomingCallRingtone();
    } else {
      stopIncomingCallRingtone();
    }

    return () => {
      setMessengerTitleIncomingCall(false);
      stopIncomingCallRingtone();
    };
  }, [availableCalls.length, callStatus]);

  // On browser close/refresh, fire a beacon to leave the active room.
  // sendBeacon is used because async fetch/axios won't complete during unload.
  useEffect(() => {
    if (!VOIP_ENABLED) return undefined;
    const handleUnload = () => {
      const room = activeRoomRef.current;
      if (!room) return;
      const url = `${process.env.PUBLIC_API_URL}/messaging/voice/rooms/${room.id}/leave`;
      navigator.sendBeacon(url);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const value = {
    isVoipEnabled: VOIP_ENABLED,
    callStatus,
    activeRoom,
    isMuted,
    availableCalls,
    incomingCall: availableCalls[0] || null,
    isCreator,
    cameraOn,
    localVideoStreamRef,
    remoteVideoStreams,
    connectingPeers,
    startCall,
    joinCall,
    leaveCall,
    endCall,
    toggleMute,
    toggleCamera,
    dismissIncomingCall,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function VoiceProvider({ children, currentUserId }) {
  return <VoiceCoordinator currentUserId={currentUserId}>{children}</VoiceCoordinator>;
}

export function useVoiceContext() {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoiceContext must be used within VoiceProvider');
  return ctx;
}
